import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva } from 'class-variance-authority'
import { cn } from '@/lib/utils'

/**
 * Button hierarchy (design-system/COMPONENTS.md):
 * - `default`: the one primary action in a view (solid accent).
 * - `secondary` / `outline`: supporting actions (surface + border).
 * - `ghost`: tertiary, table rows, chrome.
 * - `destructive-quiet`: destructive actions in place (Remove, Revoke, Void).
 * - `destructive`: solid red, only for the confirming button in a dialog.
 * - `link`: inline text action.
 *
 * Height and text follow density: 32px/13px under `data-density="compact"`
 * (dashboard, ops), 40px/15px otherwise (signer pages).
 */
const BUTTON_VARIANTS = {
  default: 'bg-primary text-primary-foreground shadow-card hover:bg-primary-hover',
  secondary: 'border border-input bg-card text-foreground shadow-card hover:bg-accent',
  outline: 'border border-input bg-card text-foreground shadow-card hover:bg-accent',
  ghost: 'text-foreground hover:bg-accent',
  'destructive-quiet': 'text-danger hover:bg-danger-subtle',
  destructive: 'bg-destructive text-destructive-foreground shadow-card hover:bg-destructive-hover',
  link: 'h-auto px-0 text-link underline-offset-4 hover:underline',
}

const BUTTON_SIZES = {
  default: 'h-control px-control-x text-control',
  sm: 'h-control-sm px-2.5 text-control',
  lg: 'h-11 px-6 text-base',
  icon: 'size-control text-control',
  'icon-sm': 'size-control-sm text-control',
}

/** Visual weight of a button. `null` is not a variant; omit the prop for the default. */
export type ButtonVariant = keyof typeof BUTTON_VARIANTS

/** Control size. `null` is not a size; omit the prop for the default. */
export type ButtonSize = keyof typeof BUTTON_SIZES

export const buttonVariants = cva(
  'inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: BUTTON_VARIANTS,
      size: BUTTON_SIZES,
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

interface ButtonOwn {
  variant?: ButtonVariant
  size?: ButtonSize
}

/** A real `<button>`. `asChild` is absent or false, so button attributes apply. */
type NativeButtonProps = ButtonOwn &
  React.ComponentProps<'button'> & {
    asChild?: false
  }

/**
 * Button styles on the single child element (Radix `Slot`). The child is an
 * element, not button-only attributes such as `type` or `form`.
 */
type SlotButtonProps = ButtonOwn &
  Omit<React.ComponentProps<typeof Slot>, 'children'> & {
    asChild: true
    children: React.ReactElement
  }

export type ButtonProps = NativeButtonProps | SlotButtonProps

function buttonClassName(
  variant: ButtonVariant | undefined,
  size: ButtonSize | undefined,
  className: string | undefined,
): string {
  return cn(buttonVariants({ variant, size }), className)
}

type ButtonParts =
  | {
      asChild: true
      className: string
      rest: Omit<SlotButtonProps, 'asChild' | 'variant' | 'size' | 'className'>
    }
  | {
      asChild?: false
      className: string
      rest: Omit<NativeButtonProps, 'asChild' | 'variant' | 'size' | 'className'>
    }

/** Pulls the element choice and styles off the props so they are not forwarded to the DOM. */
function buttonParts(props: ButtonProps): ButtonParts {
  if (props.asChild) {
    const { asChild, variant, size, className, ...rest } = props
    return { asChild, className: buttonClassName(variant, size, className), rest }
  }
  const { asChild, variant, size, className, ...rest } = props
  const resolved = buttonClassName(variant, size, className)
  // Absent `asChild` is not `false`. exactOptionalPropertyTypes rejects an explicit undefined.
  if (asChild === false) return { asChild, className: resolved, rest }
  return { className: resolved, rest }
}

/**
 * A styled button. Pass `asChild` to apply button styling to a child element
 * (e.g. a router `<Link>`) via Radix `Slot`.
 */
export function Button(props: ButtonProps) {
  const parts = buttonParts(props)
  if (parts.asChild) {
    return <Slot className={parts.className} {...parts.rest} />
  }
  return <button className={parts.className} {...parts.rest} />
}
